import { useEffect, useMemo, useRef, useState, type MouseEvent, type MutableRefObject, type ReactNode } from 'react';
import { chevronPoints, lineZone } from '@urbanrats/domain';
import type { CountingLine, NormPoint, SourceMode } from '@urbanrats/contracts';
import { useI18n, type MessageKey } from '../i18n/index.ts';
import { canvasBacking, contentRect, normToBox, type Size } from '../lib/geometry.ts';
import type { OverlayTrack } from '../gateway/index.ts';

export type TestPattern = '16:9' | '4:3' | '21:9' | '9:16';

const PATTERN_SIZE: Record<TestPattern, Size> = {
  '16:9': { w: 1600, h: 900 },
  '4:3': { w: 1600, h: 1200 },
  '21:9': { w: 2100, h: 900 },
  '9:16': { w: 900, h: 1600 },
};

/**
 * Synthetic frame (not footage) used to verify overlay alignment: the browser letterboxes the <img>
 * via object-fit, while the overlay computes the content rect independently. The magenta line is
 * painted into the image itself at the manifest line position; the lime overlay line must sit on it.
 */
function makeTestPattern(ratio: TestPattern, m: { line: CountingLine }): string {
  const { w, h } = PATTERN_SIZE[ratio];
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d');
  if (!g) return '';
  g.fillStyle = '#10161d';
  g.fillRect(0, 0, w, h);
  g.strokeStyle = '#2a3441';
  g.lineWidth = 2;
  for (let i = 1; i < 10; i++) {
    g.beginPath();
    g.moveTo((w * i) / 10, 0);
    g.lineTo((w * i) / 10, h);
    g.moveTo(0, (h * i) / 10);
    g.lineTo(w, (h * i) / 10);
    g.stroke();
  }
  g.strokeStyle = '#ffbc57';
  g.lineWidth = 8;
  g.strokeRect(4, 4, w - 8, h - 8);
  g.strokeStyle = '#ff3fd2';
  g.lineWidth = 14;
  g.beginPath();
  g.moveTo(m.line.a.x * w, m.line.a.y * h);
  g.lineTo(m.line.b.x * w, m.line.b.y * h);
  g.stroke();
  g.fillStyle = '#9aa7b6';
  g.font = `${Math.round(h / 18)}px system-ui, sans-serif`;
  g.fillText(`TEST PATTERN ${ratio} · synthetic`, w * 0.04, h * 0.1);
  return c.toDataURL('image/png');
}

export interface OverlayGeometry {
  roi: readonly NormPoint[];
  line: CountingLine;
}

interface Props {
  geometry: OverlayGeometry | null;
  sourceMode: SourceMode;
  testPattern: TestPattern | null;
  pulse: number;
  reducedMotion: boolean;
  /** Real media layer (e.g. <MediaPlayer/>) and its intrinsic frame size once known. */
  media?: { node: ReactNode; intrinsic: Size | null } | null;
  /** Extra HUD chips (source health, timecode…). */
  hud?: ReactNode;
  /** Status message over the picture (errors, uncalibrated line…). */
  notice?: ReactNode;
  /** True only when the line comes from a locked round manifest. */
  lineLocked?: boolean;
  /** Calibration: receives clicks mapped to normalised frame coordinates (letterbox aware). */
  onPick?: ((p: NormPoint) => void) | null;
  /** Filled with a drawer for PTS-released tracker boxes. */
  tracksRef?: MutableRefObject<((tracks: OverlayTrack[]) => void) | null>;
}

function drawOverlay(c: HTMLCanvasElement, box: Size, intrinsic: Size, manifest: OverlayGeometry, lit: boolean): void {
  const dpr = window.devicePixelRatio || 1;
  const backing = canvasBacking(box, dpr);
  c.width = backing.w;
  c.height = backing.h;
  const g = c.getContext('2d');
  if (!g) return;
  g.setTransform(backing.w / Math.max(box.w, 1), 0, 0, backing.h / Math.max(box.h, 1), 0, 0);
  g.clearRect(0, 0, box.w, box.h);
  const rect = contentRect(box, intrinsic, 'contain');
  if (rect.w === 0) return;

  const roi = manifest.roi.map((p) => normToBox(p, rect));
  g.beginPath();
  roi.forEach((p, i) => (i ? g.lineTo(p.x, p.y) : g.moveTo(p.x, p.y)));
  g.closePath();
  g.fillStyle = 'rgba(66, 217, 232, 0.06)';
  g.fill();
  g.setLineDash([8, 6]);
  g.strokeStyle = 'rgba(66, 217, 232, 0.8)';
  g.lineWidth = 1.5;
  g.stroke();
  g.setLineDash([]);

  const a = normToBox(manifest.line.a, rect);
  const b = normToBox(manifest.line.b, rect);
  // Approach zone with chevrons in the counted direction (display only; the line itself counts).
  const z = lineZone(manifest.line, rect.w, rect.h);
  const na = normToBox(z.near.a, rect);
  const nb = normToBox(z.near.b, rect);
  g.beginPath();
  g.moveTo(na.x, na.y);
  g.lineTo(nb.x, nb.y);
  g.lineTo(b.x, b.y);
  g.lineTo(a.x, a.y);
  g.closePath();
  g.fillStyle = 'rgba(255,210,31,0.13)';
  g.fill();
  g.strokeStyle = 'rgba(255,210,31,0.85)';
  g.lineWidth = 1.5;
  g.stroke();
  for (const c of z.chevrons) {
    // Chevron in the box's pixel space (normalised anchor mapped into the letterboxed rect).
    const pts = chevronPoints(c, rect.w, rect.h).map(([x, y]) => [rect.x + x, rect.y + y] as const);
    g.beginPath();
    g.moveTo(pts[0]![0], pts[0]![1]);
    g.lineTo(pts[1]![0], pts[1]![1]);
    g.lineTo(pts[2]![0], pts[2]![1]);
    g.strokeStyle = '#ffd21f';
    g.lineWidth = 3.5;
    g.lineCap = 'round';
    g.lineJoin = 'round';
    g.stroke();
  }
  g.strokeStyle = '#ffd21f';
  g.lineWidth = lit ? 7 : 3;
  g.lineCap = 'round';
  g.beginPath();
  g.moveTo(a.x, a.y);
  g.lineTo(b.x, b.y);
  g.stroke();
  for (const p of [a, b]) {
    g.beginPath();
    g.arc(p.x, p.y, 4, 0, Math.PI * 2);
    g.fillStyle = '#ffd21f';
    g.fill();
  }
}

export function VideoStage({ geometry, sourceMode, testPattern, pulse, reducedMotion, media, hud, notice, lineLocked = true, onPick, tracksRef }: Props) {
  const { t } = useI18n();
  const boxRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tracksCanvas = useRef<HTMLCanvasElement>(null);
  const [box, setBox] = useState<Size>({ w: 0, h: 0 });
  const drawRef = useRef<(lit: boolean) => void>(() => undefined);

  const line = geometry?.line;
  const patternUrl = useMemo(() => (!media && testPattern && line ? makeTestPattern(testPattern, { line }) : null), [media, testPattern, line]);
  const mw = media?.intrinsic?.w ?? 0;
  const mh = media?.intrinsic?.h ?? 0;
  const hasVideo = !!media;
  // Overlay geometry follows the real intrinsic frame once the player reports it.
  const intrinsic = useMemo<Size>(() => (mw > 0 && mh > 0 ? { w: mw, h: mh } : testPattern && !hasVideo ? PATTERN_SIZE[testPattern] : { w: 16, h: 9 }), [mw, mh, testPattern, hasVideo]);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      if (entry) setBox({ w: entry.contentRect.width, h: entry.contentRect.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (pulse === 0 || reducedMotion) return;
    drawRef.current(true);
    const id = setTimeout(() => drawRef.current(false), 250);
    return () => clearTimeout(id);
  }, [pulse, reducedMotion]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    if (!geometry) {
      c.getContext('2d')?.clearRect(0, 0, c.width, c.height);
      return;
    }
    drawRef.current = (lit) => drawOverlay(c, box, intrinsic, geometry, lit);
    drawOverlay(c, box, intrinsic, geometry, false);
  }, [box, geometry, intrinsic]);

  useEffect(() => {
    if (!tracksRef) return;
    tracksRef.current = (tracks) => {
      const c = tracksCanvas.current;
      if (!c) return;
      const dpr = window.devicePixelRatio || 1;
      const backing = canvasBacking(box, dpr);
      if (c.width !== backing.w || c.height !== backing.h) {
        c.width = backing.w;
        c.height = backing.h;
      }
      const g = c.getContext('2d');
      if (!g) return;
      g.setTransform(backing.w / Math.max(box.w, 1), 0, 0, backing.h / Math.max(box.h, 1), 0, 0);
      g.clearRect(0, 0, box.w, box.h);
      const rect = contentRect(box, intrinsic, 'contain');
      for (const t of tracks) {
        const p1 = normToBox({ x: t.box[0], y: t.box[1] }, rect);
        const p2 = normToBox({ x: t.box[2], y: t.box[3] }, rect);
        g.strokeStyle = t.confirmed ? 'rgba(66, 217, 232, 0.9)' : 'rgba(66, 217, 232, 0.35)';
        g.lineWidth = 1.5;
        g.setLineDash(t.confirmed ? [] : [4, 3]);
        g.strokeRect(p1.x, p1.y, p2.x - p1.x, p2.y - p1.y);
        // Bottom-centre: the point that is tested against the line.
        g.fillStyle = '#42d9e8';
        g.fillRect((p1.x + p2.x) / 2 - 2, p2.y - 2, 4, 4);
      }
      g.setLineDash([]);
    };
    return () => {
      tracksRef.current = null;
    };
  }, [tracksRef, box, intrinsic]);

  const pick = (e: MouseEvent<HTMLDivElement>) => {
    if (!onPick) return;
    const r = e.currentTarget.getBoundingClientRect();
    const rect = contentRect({ w: r.width, h: r.height }, intrinsic, 'contain');
    const x = (e.clientX - r.left - rect.x) / rect.w;
    const y = (e.clientY - r.top - rect.y) / rect.h;
    if (x < 0 || x > 1 || y < 0 || y > 1) return; // click on the letterbox bars
    onPick({ x: Math.round(x * 1000) / 1000, y: Math.round(y * 1000) / 1000 });
  };

  const hasMedia = !!patternUrl || hasVideo;
  return (
    <div className="video" ref={boxRef} data-testid="video-stage">
      {media?.node}
      {patternUrl && <img className="media" src={patternUrl} alt={t('video.testPattern', { ratio: testPattern ?? '' })} />}
      <canvas ref={tracksCanvas} className="overlay" aria-hidden />
      <canvas ref={canvasRef} className="overlay" aria-hidden />
      {onPick && <div className="pick-layer" onClick={pick} data-testid="pick-layer" aria-label="Calibration: click on the video" role="application" />}
      <div className="video-hud">
        <span className={`hud-chip mode-${sourceMode}`}>{t(`mode.${sourceMode}` as MessageKey)}</span>
        <span className="hud-chip">{t('video.clockUnknown')}</span>
        {geometry && lineLocked && (
          <span className="hud-chip hud-lock" title={t('video.lineLocked')}>
            <span aria-hidden>🔒</span>
            <span className="sr-only">{t('video.lineLocked')}</span>
          </span>
        )}
        {hud}
      </div>
      {!hasMedia && (
        <div className="no-video" role="note" data-testid="no-video">
          <strong>{t(sourceMode === 'SIMULATION' ? 'video.none.title' : 'video.server.title')}</strong>
          <span>{t(sourceMode === 'SIMULATION' ? 'video.none.body' : 'video.server.body')}</span>
        </div>
      )}
      {testPattern && !hasVideo && <div className="pattern-note">{t('video.testPattern', { ratio: testPattern })}</div>}
      {notice && <div className="video-notice">{notice}</div>}
    </div>
  );
}
